import {MenuItem, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import {useQueryClient} from '@tanstack/react-query';
import {GetProgressResponse} from 'domain/usecases/private/profile/get-progress';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {ServicesTaggingEnum} from 'main/analytics/gaTagsEnum';
import {
	FavoritesEnum,
	FeaturesEnum,
	HelpCenterEnum,
	ProfileEnum,
	SelectionFlowEnum,
	TokenEnum,
} from 'presentation/constants';
import {useContractStatus, useHostName} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {useAccountInformation, useMediaQueries} from 'presentation/pages';
import {useProfileSectionStore} from 'presentation/pages/private/profile/store/useProfileSection';
import {PROFILE_SECTION} from 'presentation/pages/private/profile/store/useProfileSection/constants';
import {ProfileSection} from 'presentation/pages/private/profile/store/useProfileSection/types';
import {translations} from 'presentation/translations-new';
import React, {useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {ProfileMenuEnum} from '../../../../../tests/constants/ProfileMenuEnum';
import {BeneficiaryInfo} from './components';
import {CompleteProfileCard} from './components/CompleteProfileCard';
import {PROFILE_PROGRESS_THRESHOLDS} from './components/CompleteProfileCard/constants';
import {ProfileMenuItem} from './components/ProfileMenuItem';
import {QuickAction} from './components/QuickAction';
import {WhoIndicates} from './components/WhoIndicates';
import {ProfileMenuContentProps} from './types';
import {getPendingPercentage} from 'presentation/utils/getPendingPercentage';
import {useShowFacialBiometric} from 'presentation/pages/private/hooks/useShowFacialBiometric';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
const {registerChange, logOut, loginAndPrivacy, help, token} =
	translations['pt-br'].others.profileMenu;
const {favorites} = translations['pt-br'].privateFlow.services;

export const ProfileMenuContent = ({onClose}: ProfileMenuContentProps) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const {
		isB2BAccess,
		isResponsibleWithoutBenefits,
		handleLogout,
		accesses,
		isHolder,
		odontoOnly,
		canChangePassword,
		canChangeAddress,
		beneficiary,
		isEarlyAccess,
		healthOnly,
		flPlanoSomenteHospitalar,
	} = useAccountInformation();
	const {isGndiSaoPaulo, isNotredameSaude} = useHostName();
	const {isValid, isCancelledAtOneYear, isContractSuspended} = useContractStatus();
	const isLowHeight = useMediaQuery('(max-height:700px)');
	const {isMobile} = useMediaQueries();
	const showBankData = accesses.refund && isHolder && !odontoOnly && isGndiSaoPaulo;
	const showFacialBiometric = useShowFacialBiometric();
	const setActiveSection = useProfileSectionStore(state => state.setActiveSection);
	const setIsProfileCompleted = useProfileSectionStore(state => state.setIsProfileCompleted);
	const isProfileCompleted = useProfileSectionStore(state => state.isProfileCompleted);

	const {isEnabled: isFavoriteDoctorsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_FAVORITE_DOCTORS,
	);

	const {isEnabled: isHideRecommendationEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_HIDE_RECOMMENDATION,
	);

	const SHOW_FAVORITE_DOCTORS =
		isFavoriteDoctorsEnabled &&
		isValid &&
		!beneficiary.flTitularSemDireito &&
		!isEarlyAccess &&
		!isContractSuspended &&
		healthOnly &&
		!flPlanoSomenteHospitalar;

	const SHOW_TOKEN_BUTTON =
		isValid && !isResponsibleWithoutBenefits && !isB2BAccess && isGndiSaoPaulo;
	const SHOW_HELP = (isValid && !isB2BAccess) || (isCancelledAtOneYear && !isB2BAccess);

	const shouldHideWhoIndicates =
		(isLowHeight && !isMobile) || (isNotredameSaude && isHideRecommendationEnabled);
	const SHOW_WHO_INDICATES = !isB2BAccess && isValid && !shouldHideWhoIndicates;
	const handleGoToProfile = (section: ProfileSection) => {
		setActiveSection(section);
		navigate(ProfileEnum.PROFILE);
		onClose();
	};

	const queryClient = useQueryClient();

	const progressData: GetProgressResponse | undefined = queryClient.getQueryData([
		QUERY_KEYS.PROGRESS_PROFILE,
		showBankData,
		canChangeAddress,
		showFacialBiometric,
	]);

	const pendingPercentage = getPendingPercentage({
		totalFields: progressData?.totalFields || 0,
		filledFields: progressData?.filledFields || 0,
	});

	const hasShownCompleteProfileCardRef = useRef(false);

	const hasPendingProfile = progressData && !isProfileCompleted;

	if (hasPendingProfile) {
		hasShownCompleteProfileCardRef.current = true;
	}

	const showCompleteProfileCard = hasShownCompleteProfileCardRef.current;
	const showLoginAndPrivacy = (isGndiSaoPaulo && isB2BAccess) || canChangePassword;

	return (
		<Stack gap={2} justifyContent={isMobile ? 'space-between' : 'flex-start'} height="100%">
			<Stack gap={2}>
				{isMobile ? (
					<Stack alignItems="flex-end">
						<Icon
							name="close-default"
							color={theme.palette.primary.main}
							onClick={onClose}
						/>
					</Stack>
				) : null}

				<BeneficiaryInfo
					hasSocialNameToolTip
					isResponsibleWithoutBenefits={isResponsibleWithoutBenefits}
					goTo={() => {
						navigate(SelectionFlowEnum.PLAN_SELECTION);
						onClose();
						setIsProfileCompleted(false);
					}}
				/>

				<Stack direction="row" justifyContent="space-between" gap={1}>
					{SHOW_TOKEN_BUTTON ? (
						<QuickAction
							iconName="key"
							label={token}
							onClick={() => {
								navigate(TokenEnum.TOKEN);
								onClose();
							}}
						/>
					) : null}
					{SHOW_FAVORITE_DOCTORS ? (
						<QuickAction
							iconName="heart-outline"
							label={favorites}
							onClick={() => {
								navigate(FavoritesEnum.FAVORITE_DOCTORS);
								onClose();
							}}
						/>
					) : null}
					{SHOW_HELP ? (
						<QuickAction
							iconName="help-default"
							label={help}
							onClick={() => {
								navigate(HelpCenterEnum.HELP);
								onClose();
							}}
						/>
					) : null}
				</Stack>
				{showCompleteProfileCard ? (
					<CompleteProfileCard percentage={pendingPercentage} />
				) : null}
				<Stack
					sx={{
						backgroundColor: theme.palette.background.default,
						p: 2,
						gap: 3,
						borderRadius: 1.5,
						mt: isMobile ? -0.5 : 0,
					}}>
					{!isB2BAccess ? (
						<ProfileMenuItem
							data-tag-id={ServicesTaggingEnum.CLICK_MEU_PERFIL}
							onClick={() => handleGoToProfile(PROFILE_SECTION.REGISTRATION_DATA)}
							data-testid={ProfileMenuEnum.MY_PROFILE}
							label={registerChange}
							iconName="person-outline"
							showErrorIcon={pendingPercentage < PROFILE_PROGRESS_THRESHOLDS.MAX}
						/>
					) : null}
					{showLoginAndPrivacy ? (
						<ProfileMenuItem
							data-tag-id={ServicesTaggingEnum.CLICK_MEU_PERFIL}
							onClick={() => handleGoToProfile(PROFILE_SECTION.LOGIN_AND_PRIVACY)}
							data-testid={ProfileMenuEnum.MY_PROFILE}
							label={loginAndPrivacy}
							iconName="shield-encrypted"
						/>
					) : null}
				</Stack>
			</Stack>
			<Stack gap={isMobile ? 1.5 : 2}>
				{SHOW_WHO_INDICATES ? <WhoIndicates /> : null}
				<Stack
					sx={{
						backgroundColor: theme.palette.background.default,
						p: 1,
						borderRadius: 1.5,
					}}>
					<MenuItem
						data-test="logout-button"
						data-testid={ProfileMenuEnum.LOGOUT_BUTTON}
						onClick={() => {
							handleLogout();
							setIsProfileCompleted(false);
						}}
						sx={{p: 1}}>
						<Icon
							name="exit-to-app-filled"
							color={theme.palette.error.main}
							size={20}
						/>
						<Typography variant="body2" color={theme.palette.error.main} sx={{ml: 2}}>
							{logOut}
						</Typography>
					</MenuItem>
				</Stack>
			</Stack>
		</Stack>
	);
};
